import produce from 'immer';
import type { IRootDispatch, RootState } from './models';
import nonCriticalException from '../modules/exceptionLogger';

import { isSmartPricingEligible } from '../modules/consumer/Booking';
import {
  getProvider,
  isProvider,
  PrivilegedProvider,
  PublicProvider,
} from '../api/Providers';

export type State = {
  // activeProviderId is used on the pro side to demarcate the current logged in provider info
  // Or for a superuser, the current pro that they are viewing
  activeProviderId?: number;
  providersById: {
    [key: number]: {
      loading: boolean;
      result?: PublicProvider | PrivilegedProvider;
      isSmartPriceEligible?: boolean;
    };
  };
};

/**
 * This is a model to store providers from /api/v2/providers/{id}
 * This endpoint will return either a Provider or a PrivilegedProvider depending on user status
 *
 * @TODO - split this model to be separate for a provider vs a client
 */
const model = {
  name: 'providers',

  state: {
    providersById: {},
  } as State,

  reducers: {
    onLoading: produce((state: State, payload: { providerId: number }) => {
      const { providerId } = payload;
      state.providersById[providerId] = { loading: true };
    }),
    onLoaded: produce((state: State, payload: PublicProvider | PrivilegedProvider) => {
      const { id } = payload;
      state.providersById[id] = {
        loading: false,
        result: payload,
        isSmartPriceEligible: payload.can_process_payments && payload.smart_pricing_enabled,
      };
    }),
    onFailed: produce((state: State, payload: { providerId: number }) => {
      const { providerId } = payload;
      state.providersById[providerId] = { loading: false };
    }),

    setActiveProviderId(state: State, payload: number) {
      return {
        ...state,
        activeProviderId: payload,
      };
    },
  },

  effects: (dispatch: IRootDispatch) => ({
    loadProvider: async (
      payload: { providerId: number, noCache?: boolean, setActive?: boolean },
      rootState: RootState,
    ): Promise<PublicProvider | PrivilegedProvider> => {
      const {
        providerId, noCache, setActive,
      } = payload;
      const existingRecord = rootState.providers.providersById[providerId];

      if (noCache || !existingRecord || !existingRecord.loading || !existingRecord.result) {
        try {
          await dispatch.providers.onLoading({ providerId });
          const providerResult = await getProvider(providerId);

          if (isProvider(providerResult)) {
            if (setActive) {
              dispatch.providers.setActiveProviderId(providerId);
            }
            await dispatch.providers.onLoaded(providerResult);
            return providerResult;
          }

          throw new Error('Error occurred retrieving provider.');
        } catch (e) {
          nonCriticalException(e);
          await dispatch.providers.onFailed({ providerId });
          return null;
        }
      }
      if (setActive) {
        dispatch.providers.setActiveProviderId(providerId);
      }
      return existingRecord.result;
    },
  }),

  selectors: (slice: any) => ({
    activeProvider() {
      return slice((state: State) => (
        state.providersById[state.activeProviderId]?.result
      ));
    },

    isSmartPricingEligible() {
      return slice(isSmartPricingEligible);
    },
  }),
};

export default model;

export const selectors = {
  activeProvider: (state: RootState) => state
    .providers
    .providersById[state.providers.activeProviderId]?.result,
};
